import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, Clock, ArrowLeft, ArrowRight, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
import CTABanner from "@/components/CTABanner";
import articles, { getArticle } from "./articles";

const ORIGIN = "https://aussieinspections.com.au";

const formatDate = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

function Block({ block }) {
  switch (block.type) {
    case "heading":
      return (
        <h2 className="font-chivo text-2xl md:text-3xl font-bold text-slate-900 mt-12 mb-4 tracking-tight">
          {block.text}
        </h2>
      );
    case "list":
      return (
        <ul className="my-6 space-y-3">
          {block.items.map((item, i) => (
            <li key={i} className="flex gap-3 text-lg leading-relaxed text-slate-700">
              <span aria-hidden="true" className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      );
    case "callout":
      return (
        <p className="my-8 border-l-4 border-primary bg-slate-50 px-6 py-5 text-lg font-medium leading-relaxed text-slate-900 rounded-r-lg">
          {block.text}
        </p>
      );
    default:
      return <p className="my-5 text-lg leading-relaxed text-slate-700">{block.text}</p>;
  }
}

export default function ArticlePage() {
  const { slug } = useParams();
  const article = getArticle(slug);

  // Unknown slug falls through to the 404 route rather than rendering an empty
  // article shell — a soft 404 here would be indexed as thin content.
  if (!article) return <Navigate to="/404" replace />;

  const url = `${ORIGIN}/blog/${article.slug}`;
  const related = articles.filter((a) => a.slug !== article.slug).slice(0, 3);

  // Two graphs: the Article itself, and the FAQPage built from this article's
  // own Q&As. Both are what an answer engine reads to decide it can quote us.
  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        "headline": article.title,
        "description": article.description,
        "datePublished": article.publishedAt,
        "dateModified": article.updatedAt,
        "inLanguage": "en-AU",
        "mainEntityOfPage": { "@type": "WebPage", "@id": url },
        "url": url,
        "articleSection": article.category,
        "author": {
          "@type": "Organization",
          "name": "Aussie Inspections",
          "url": ORIGIN,
        },
        "publisher": {
          "@type": "Organization",
          "name": "Aussie Inspections",
          "url": ORIGIN,
          "logo": { "@type": "ImageObject", "url": `${ORIGIN}/logo.png` },
        },
        "about": {
          "@type": "Service",
          "serviceType": "Building and property inspections",
          "areaServed": [
            { "@type": "City", "name": "Darwin" },
            { "@type": "City", "name": "Palmerston" },
            { "@type": "AdministrativeArea", "name": "Litchfield" },
            { "@type": "AdministrativeArea", "name": "Wagait" },
          ],
          "provider": {
            "@type": "LocalBusiness",
            "name": "Aussie Inspections",
            "url": ORIGIN,
            "telephone": "+61413245519",
          },
        },
      },
      {
        "@type": "FAQPage",
        "mainEntity": article.faq.map((f) => ({
          "@type": "Question",
          "name": f.q,
          "acceptedAnswer": { "@type": "Answer", "text": f.a },
        })),
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": ORIGIN },
          { "@type": "ListItem", "position": 2, "name": "Guides", "item": `${ORIGIN}/blog` },
          { "@type": "ListItem", "position": 3, "name": article.title, "item": url },
        ],
      },
    ],
  };

  return (
    <div>
      <SEO
        title={article.title}
        description={article.description}
        url={`/blog/${article.slug}`}
        type="article"
        schema={schema}
      />

      <CTABanner />

      <article>
        <header className="bg-primary text-white py-16 md:py-20">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <p className="text-blue-200 font-semibold tracking-wide mb-4 uppercase text-sm">
                {article.category}
              </p>
              <h1 className="font-chivo text-4xl md:text-5xl font-black tracking-tight leading-[1.1] mb-6">
                {article.title}
              </h1>
              <p className="text-xl text-blue-100 leading-relaxed mb-8">{article.description}</p>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-blue-200 text-sm">
                <span className="inline-flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <time dateTime={article.publishedAt}>{formatDate(article.publishedAt)}</time>
                </span>
                <span className="inline-flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  {article.readTime}
                </span>
              </div>
            </motion.div>
          </div>
        </header>

        <div className="py-16 md:py-20 bg-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            {article.content.map((block, i) => (
              <Block key={i} block={block} />
            ))}

            {article.faq.length > 0 && (
              <section className="mt-16 pt-12 border-t border-slate-200">
                <h2 className="font-chivo text-2xl md:text-3xl font-bold text-slate-900 mb-8 tracking-tight">
                  Common questions
                </h2>
                <dl className="space-y-8">
                  {article.faq.map((f, i) => (
                    <div key={i}>
                      <dt className="font-semibold text-slate-900 text-lg mb-2">{f.q}</dt>
                      <dd className="text-lg leading-relaxed text-slate-700">{f.a}</dd>
                    </div>
                  ))}
                </dl>
              </section>
            )}

            {article.sources.length > 0 && (
              <section className="mt-16 pt-12 border-t border-slate-200">
                <h2 className="font-chivo text-xl font-bold text-slate-900 mb-4 tracking-tight">
                  Sources
                </h2>
                <p className="text-slate-600 mb-4 text-sm leading-relaxed">
                  Regulatory information on this page is drawn from the following primary sources.
                  Requirements change — confirm your own position with the relevant authority.
                </p>
                <ul className="space-y-2">
                  {article.sources.map((s, i) => (
                    <li key={i}>
                      <a
                        href={s.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-start gap-2 text-primary hover:underline"
                      >
                        <ExternalLink className="h-4 w-4 mt-1 shrink-0" />
                        <span>{s.label}</span>
                      </a>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <section className="mt-16 rounded-2xl bg-slate-50 border border-slate-200 p-8 text-center">
              <h2 className="font-chivo text-2xl font-bold text-slate-900 mb-3 tracking-tight">
                Need an inspection in the Top End?
              </h2>
              <p className="text-slate-600 mb-6 text-lg">
                Flat rate from $260 incl. GST. Digital report within 72 hours.
              </p>
              <div className="flex flex-wrap justify-center gap-3">
                <Button asChild size="lg">
                  <Link to="/contact">Book an inspection</Link>
                </Button>
                <Button asChild size="lg" variant="outline">
                  <a href="tel:+61413245519">Call 0413 24 55 19</a>
                </Button>
              </div>
            </section>
          </div>
        </div>
      </article>

      <section className="py-16 bg-slate-50 border-t border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <h2 className="font-chivo text-2xl font-bold text-slate-900 tracking-tight">
              More guides
            </h2>
            <Link to="/blog" className="inline-flex items-center gap-2 text-primary font-semibold hover:underline">
              <ArrowLeft className="h-4 w-4" /> All guides
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {related.map((a) => (
              <Link
                key={a.slug}
                to={`/blog/${a.slug}`}
                className="group block rounded-xl border border-slate-200 bg-white p-6 hover:border-primary transition-colors"
              >
                <p className="text-xs font-semibold uppercase tracking-wide text-primary mb-2">
                  {a.category}
                </p>
                <h3 className="font-semibold text-slate-900 mb-2 leading-snug group-hover:text-primary transition-colors">
                  {a.title}
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed mb-3">{a.description}</p>
                <span className="inline-flex items-center gap-1 text-sm font-semibold text-primary">
                  Read <ArrowRight className="h-4 w-4" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
